import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronUp,
  FaFacebook,
  FaGoogle,
  FaInstagram,
  FaLinkedin,
  FaPlug,
  FaSync,
  FaTrash,
  FaYoutube,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import {
  AdAccountsCard,
  CredentialsCard,
  GoogleLeadFormsCard,
  HealthPill,
  PartnerAssetsCard,
  SignInActions,
  TummaAppsCard,
  WhatsAppNumbersCard,
} from "./AdsSetup";
import { Empty, PageHead } from "./WorkUi";
import { errorMessage } from "./workApi";
import "../dm/dm.css";
import "./work.css";

// One row per platform: sign in, or open the row to add the account with an id + token.
const CONNECT_ROWS = [
  {
    platform: "Facebook",
    provider: "meta",
    icon: FaFacebook,
    note: "Pages you manage",
    idLabel: "Page ID",
    idHint: "Page > About > Page transparency, or Meta Business Suite > Settings > Pages",
    nameLabel: "Page name",
    tokenLabel: "Page access token",
    tokenHint: "Business settings > System users > Generate token",
  },
  {
    platform: "Instagram",
    provider: "meta",
    icon: FaInstagram,
    note: "Business accounts linked to your Pages (sign in with Facebook)",
    idLabel: "Instagram business account ID",
    idHint: "Meta Business Suite > Settings > Instagram accounts",
    nameLabel: "Username",
    tokenLabel: "Access token",
    tokenHint: "Token of the Facebook Page linked to this account",
  },
  {
    platform: "LinkedIn",
    provider: "linkedin",
    icon: FaLinkedin,
    note: "Your LinkedIn profile or company page",
    idLabel: "Organization / member ID",
    idHint: "The number in urn:li:organization:12345",
    nameLabel: "Page name",
    tokenLabel: "Access token",
  },
  {
    platform: "YouTube",
    provider: "youtube",
    icon: FaYoutube,
    note: "Channels of a Google account",
    idLabel: "Channel ID",
    idHint: "Starts with UC… (YouTube Studio > Settings > Channel > Advanced)",
    nameLabel: "Channel name",
    tokenLabel: "Access token",
  },
];
const EMPTY_TOKEN = { External_Account_Id: "", Account_Name: "", Access_Token: "" };
const PROVIDER_LABELS = { meta: "Facebook", linkedin: "LinkedIn", youtube: "Google", metaads: "Facebook", googleads: "Google" };

function fmtWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}

/** Inner table under an account row. */
function ConnectionDetails({ c, partnerName }) {
  const signedIn =
    c.Source === "partner"
      ? `${partnerName} partner access`
      : c.Source === "token" || !c.Provider
        ? "Token"
        : PROVIDER_LABELS[c.Provider] || c.Provider;
  const rows = [
    ["Account / page ID", c.External_Account_Id || "—"],
    ["Used for", c.Kind === "ads" ? "Ad numbers" : "Publishing posts and comments"],
    ["Signed in with", signedIn],
    ["Status", c.Status || "—"],
    ["Sign-in expires", c.Token_Expires_On ? fmtWhen(c.Token_Expires_On) : "Does not expire"],
    ["Renews by itself", c.Refreshable ? "Yes" : "No"],
    ["Last checked", fmtWhen(c.Last_Checked_On)],
    ["Connected on", fmtWhen(c.Created_On)],
  ];
  if (c.Last_Error) rows.push(["Last error", c.Last_Error]);
  return (
    <table className="wk-subtable">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td className={label === "Last error" ? "wk-subtable__err" : undefined}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
const PLATFORM_ICONS = {
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  LinkedIn: FaLinkedin,
  YouTube: FaYoutube,
  MetaAds: FaFacebook,
  GoogleAds: FaGoogle,
};
const PLATFORM_LABELS = { MetaAds: "Meta ads", GoogleAds: "Google Ads" };

/**
 * Connect social accounts once; approved posts can then be published automatically.
 * ``embedded`` (e.g. inside a client's Setup tab) uses ``tenantId`` from the page: no title, client picker or
 * Tumma's own app settings.
 * ``mode`` splits the page: "setup" (Social Connections > API Connections: connect, tokens, remove, app credentials,
 * webhooks) and "health" (Sync & Health: sign-in checks, syncing, pausing; Reconnect opens ``setupHref``).
 * "all" (default) shows both.
 */
function SocialConnections({ api, tenantId: fixedTenantId = null, embedded = false, mode = "all", setupHref = null }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const showSetup = mode !== "health";
  const showSync = mode !== "setup" && !embedded;
  const [pickedTenantId, setTenantId] = useState("");
  const tenantId = fixedTenantId ? String(fixedTenantId) : pickedTenantId;
  const [clients, setClients] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState(EMPTY_TOKEN);
  const [tokenRow, setTokenRow] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleTokenRow = (platform) => {
    setTokenRow((open) => (open === platform ? null : platform));
    setManual(EMPTY_TOKEN);
  };

  useEffect(() => {
    const message = params.get("message");
    if (message) {
      (params.get("connected") === "1" ? notify.success : notify.error)(message);
      const rest = new URLSearchParams(params);
      rest.delete("message");
      rest.delete("connected");
      setParams(rest, { replace: true });
    }
  }, [params, setParams, notify]);

  useEffect(() => {
    if (!api.isStaff || fixedTenantId) return;
    api
      .projects({ limit: 100 })
      .then((rows) => {
        const map = new Map();
        (rows || []).forEach((p) => p.tenant_id && map.set(p.tenant_id, p.Tenant_Name || `Client #${p.tenant_id}`));
        setClients(Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => null);
  }, [api, fixedTenantId]);

  const load = useCallback(async () => {
    if (api.isStaff && !tenantId) {
      setData({ connections: [], providers: {} });
      return;
    }
    try {
      setData(await api.connections(api.isStaff ? { tenant_id: tenantId } : undefined));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load API connections"));
    }
  }, [api, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async (provider) => {
    if (!showSetup) {
      if (setupHref) navigate(setupHref);
      return;
    }
    try {
      const res = await api.oauthStart(provider, api.isStaff ? { tenant_id: tenantId } : undefined);
      window.location.assign(res.url);
    } catch (err) {
      notify.error(errorMessage(err, "Could not start connecting"));
    }
  };

  const remove = async (c) => {
    if (!(await confirm({ title: "Disconnect", message: `Disconnect ${c.Platform}: ${c.Account_Name}? Scheduled auto-posts to it will fail.` }))) return;
    try {
      await api.deleteConnection(c.Connection_Id);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.addConnection({ ...manual, Platform: tokenRow, tenant_id: api.isStaff ? tenantId : undefined });
      notify.success(`${tokenRow} account connected`);
      setManual(EMPTY_TOKEN);
      setTokenRow(null);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const [checking, setChecking] = useState(null);
  const checkOne = async (c) => {
    setChecking(c.Connection_Id);
    try {
      const res = await api.checkConnection(c.Connection_Id);
      (res.Health === "ok" ? notify.success : notify.error)(
        res.Health === "ok" ? `${res.Account_Name} is working` : res.Last_Error || `${res.Account_Name} needs attention`
      );
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not check"));
    } finally {
      setChecking(null);
    }
  };

  const checkAll = async () => {
    setChecking("all");
    try {
      const res = await api.checkConnections(api.isStaff ? { tenant_id: tenantId } : {});
      const bad = res.expired + res.error + res.expiring;
      (bad ? notify.error : notify.success)(
        `${res.ok} working` +
          (res.expiring ? `, ${res.expiring} to sign in again soon` : "") +
          (res.expired ? `, ${res.expired} need Reconnect` : "") +
          (res.error ? `, ${res.error} could not be checked` : "")
      );
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not check"));
    } finally {
      setChecking(null);
    }
  };

  const syncNow = async () => {
    try {
      const res = await api.syncEngagement(api.isStaff ? { tenant_id: tenantId } : {});
      notify.success(`Synced ${res.posts} post(s): ${res.new_comments} new comment(s), ${res.new_leads} new lead(s)`);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not sync"));
    }
  };

  const needsClient = api.isStaff && !tenantId;
  const partnerName = data?.credentials?.partner?.name || "Digix Leads";
  const [assignedTick, setAssignedTick] = useState(0);
  const onPartnerAssigned = () => {
    load();
    setAssignedTick((t) => t + 1);
  };

  const accountsHint = embedded
    ? "Connect the pages, channels and profiles Cloud Office publishes to. Every morning it checks each sign-in and tells you when one needs Reconnect."
    : mode === "setup"
      ? "Pages, channels and profiles Cloud Office publishes to. Connect them on the right; Sync & Health shows how each one is doing."
      : "Every hour Cloud Office pulls views, likes, comments and lead-form leads for posts published here. Every morning it checks each sign-in, renews it where it can, and tells you when one needs Reconnect.";

  return (
    <div className={`dmp wk${embedded || mode === "setup" ? " wk--embedded" : ""}`}>
      {embedded || mode === "setup" ? null : (
      <PageHead title={mode === "health" ? "Sync & Health" : "API Connections"}>
        {api.isStaff ? (
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            aria-label="Client"
            style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", minHeight: 38 }}
          >
            <option value="">Pick a client</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : null}
      </PageHead>
      )}
      {error ? <div className="dmp-error">{error}</div> : null}
      {needsClient ? (
        <div className="dmp-card">
          <Empty icon={FaPlug} title="Pick a client to see or connect their accounts" />
        </div>
      ) : (
        <div className={showSetup ? "wk-grid-2" : undefined}>
          <div className="dmp-card">
            <h3 className="dmp-card__title">
              Accounts
              {data?.connections?.length ? (
                <span style={{ display: "inline-flex", gap: 6 }}>
                  <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={checkAll} disabled={checking === "all"}>
                    <FaSync aria-hidden="true" /> {checking === "all" ? "Checking…" : "Check all"}
                  </button>
                  {showSync ? (
                    <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={syncNow}>
                      Sync results now
                    </button>
                  ) : null}
                </span>
              ) : null}
            </h3>
            <p className="wk-muted" style={{ marginTop: -6 }}>
              {accountsHint}
            </p>
            {data?.connections?.length ? (
              <table className="wk-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.connections.map((c) => {
                    const Icon = PLATFORM_ICONS[c.Platform] || FaPlug;
                    const partner = c.Source === "partner";
                    const canReconnect = !partner && c.Provider && data.providers?.[c.Provider] && (showSetup || setupHref);
                    const open = detailsId === c.Connection_Id;
                    return (
                      <React.Fragment key={c.Connection_Id}>
                      <tr className={open ? "wk-row-open" : undefined}>
                        <td>
                          <Icon aria-hidden="true" /> <strong>{PLATFORM_LABELS[c.Platform] || c.Platform}</strong> · {c.Account_Name}
                          {c.Kind === "ads" ? <div className="wk-muted" style={{ fontSize: 12 }}>Used for ad numbers</div> : null}
                          {partner ? <div className="wk-muted" style={{ fontSize: 12 }}>Managed by {partnerName}</div> : null}
                          <div>
                            <button
                              type="button"
                              className="wk-row-link"
                              aria-expanded={open}
                              onClick={() => setDetailsId(open ? null : c.Connection_Id)}
                            >
                              {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                              {open ? "Hide details" : "Details"}
                            </button>
                          </div>
                        </td>
                        <td>
                          <HealthPill
                            health={c.Health}
                            expiresOn={c.Token_Expires_On}
                            checkedOn={c.Last_Checked_On}
                            error={c.Last_Error}
                            refreshable={c.Refreshable}
                          />
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <SignInActions
                            busy={checking === c.Connection_Id}
                            onCheck={() => checkOne(c)}
                            onReconnect={canReconnect ? () => connect(c.Provider) : null}
                            needsReconnect={c.Health === "expired" || c.Health === "expiring"}
                          />
                          {showSetup ? (
                            <>
                              {" "}
                              <button type="button" className="dmp-link" onClick={() => remove(c)} aria-label={`Disconnect ${c.Account_Name}`}>
                                <FaTrash aria-hidden="true" />
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                      {open ? (
                        <tr className="wk-subrow">
                          <td colSpan={3}>
                            <ConnectionDetails c={c} partnerName={partnerName} />
                          </td>
                        </tr>
                      ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <Empty icon={FaPlug} title="No accounts connected yet">
                {showSetup
                  ? "Until then, use the ready-to-post pack and tap Mark posted after posting."
                  : "Connect them in Social Connections > API Connections. Until then, use the ready-to-post pack and tap Mark posted after posting."}
                {!showSetup && setupHref ? (
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => navigate(setupHref)}>
                      Open API Connections
                    </button>
                  </div>
                ) : null}
              </Empty>
            )}
          </div>
          {showSetup ? (
          <div className="dmp-card">
            <h3 className="dmp-card__title">Connect</h3>
            <p className="wk-muted" style={{ marginTop: -6 }}>
              Sign in, or open <strong>Add with token</strong> on a row to enter the account ID and token yourself.
            </p>
            <table className="wk-table wk-connect">
              <tbody>
                {CONNECT_ROWS.map((p) => {
                  const Icon = p.icon;
                  const enabled = data?.providers?.[p.provider];
                  const open = tokenRow === p.platform;
                  const set = (key) => (e) => setManual((m) => ({ ...m, [key]: e.target.value }));
                  return (
                    <React.Fragment key={p.platform}>
                      <tr className={open ? "wk-row-open" : undefined}>
                        <td className="wk-connect__icon">
                          <Icon aria-hidden="true" size={20} />
                        </td>
                        <td>
                          <strong>{p.platform}</strong>
                          <div className="wk-muted" style={{ fontSize: 12 }}>
                            {enabled
                              ? `${p.note} · ${data?.providers?.[`${p.provider}_source`] === "vault" ? "your own app" : "Tumma's app"}`
                              : "Sign-in not set up: add your own app in Vault, or use a token"}
                          </div>
                          <button
                            type="button"
                            className="wk-row-link"
                            aria-expanded={open}
                            onClick={() => toggleTokenRow(p.platform)}
                          >
                            {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                            {open ? "Close" : "Add with token"}
                          </button>
                        </td>
                        <td className="wk-connect__action">
                          <button
                            type="button"
                            className="dmp-btn dmp-btn--sm"
                            disabled={!enabled}
                            onClick={() => connect(p.provider)}
                          >
                            Sign in
                          </button>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="wk-subrow">
                          <td colSpan={3}>
                            <form className="wk-token-form" onSubmit={addManual}>
                              <label className="wk-field" title={p.idHint}>
                                {p.idLabel}
                                <input value={manual.External_Account_Id} onChange={set("External_Account_Id")} required autoFocus />
                                {p.idHint ? <span className="wk-field__hint">{p.idHint}</span> : null}
                              </label>
                              <label className="wk-field">
                                {p.nameLabel}
                                <input value={manual.Account_Name} onChange={set("Account_Name")} />
                              </label>
                              <label className="wk-field" title={p.tokenHint}>
                                {p.tokenLabel}
                                <input
                                  type="password"
                                  autoComplete="off"
                                  value={manual.Access_Token}
                                  onChange={set("Access_Token")}
                                  required
                                />
                                <span className="wk-field__hint">
                                  {p.tokenHint ? `${p.tokenHint}. ` : ""}Stored encrypted; never shown again.
                                </span>
                              </label>
                              <div className="wk-token-form__actions">
                                <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => toggleTokenRow(p.platform)}>
                                  Cancel
                                </button>
                                <button type="submit" className="dmp-btn dmp-btn--sm" disabled={saving}>
                                  {saving ? "Adding…" : `Add ${p.platform} account`}
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          ) : null}
        </div>
      )}
      {!needsClient && data && showSetup && api.isStaff ? (
        <div style={{ marginTop: 16 }}>
          <PartnerAssetsCard api={api} tenantId={tenantId} partnerName={partnerName} onAssigned={onPartnerAssigned} />
        </div>
      ) : null}
      {!needsClient && data && showSetup ? (
        <div className="wk-grid-2" style={{ marginTop: 16 }}>
          <div>
            <AdAccountsCard api={api} tenantId={tenantId} providers={data.providers} onConnect={connect} setupOnly={!showSync} />
            <WhatsAppNumbersCard
              key={`wa-${assignedTick}`}
              api={api}
              tenantId={tenantId}
              providers={data.providers}
              urls={data.urls}
              metaSource={data.credentials?.meta?.source}
              partnerName={partnerName}
              onConnect={connect}
            />
            <GoogleLeadFormsCard api={api} tenantId={tenantId} />
          </div>
          <CredentialsCard data={data} />
        </div>
      ) : null}
      {!needsClient && data && !showSetup ? (
        <div className="wk-grid-2" style={{ marginTop: 16 }}>
          <AdAccountsCard api={api} tenantId={tenantId} providers={data.providers} onConnect={connect} syncOnly />
          <WhatsAppNumbersCard
            api={api}
            tenantId={tenantId}
            providers={data.providers}
            partnerName={partnerName}
            onConnect={setupHref ? connect : null}
            syncOnly
          />
        </div>
      ) : null}
      {api.isStaff && !embedded && mode === "all" ? (
        <div style={{ marginTop: 16 }}>
          <TummaAppsCard api={api} />
        </div>
      ) : null}
    </div>
  );
}

export default SocialConnections;
